import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  Building2,
  CheckCircle2,
  LogOut,
  ChevronDown,
  ChevronRight,
  FileText,
  ClipboardCheck,
  Briefcase,
  Globe,
  Package,
  Eye,
  SlidersHorizontal,
  RadioTower,
  Construction,
  Users,
  Newspaper,
  Mail,
  ShieldCheck,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const navItems = [{ title: "Home", url: "/home", icon: Home }] as const;

export function AppSidebar() {
  const { logout } = useAuth();
  const location = useLocation();
  const isValidationActive = location.pathname.startsWith("/validation");
  const isSimulationActive =
    location.pathname.startsWith("/simulation") ||
    location.pathname.startsWith("/contextual-sim") ||
    location.pathname.startsWith("/catalog-runs");
  const isProductsActive = location.pathname.startsWith("/products");
  const isSourcesActive = location.pathname.startsWith("/sources");
  const isUsersActive = location.pathname.startsWith("/users");
  const isBusinessesActive =
    location.pathname.startsWith("/businesses") ||
    location.pathname.startsWith("/newsletter");

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-2">
          <span className="font-semibold text-primary">NVG Nexus Tools</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Tools</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <Collapsible
                defaultOpen={isValidationActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton>
                      <CheckCircle2 className="size-4" />
                      <span>Validation</span>
                      <ChevronDown className="ml-auto size-4 hidden group-data-[state=open]/collapsible:inline" />
                      <ChevronRight className="ml-auto size-4 group-data-[state=open]/collapsible:hidden" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/validation"}
                        >
                          <NavLink
                            to="/validation"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Issues
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={
                            location.pathname === "/validation/agent-prompt"
                          }
                        >
                          <NavLink
                            to="/validation/agent-prompt"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            <FileText className="size-4" />
                            Agent Prompt
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/review"
                    className={({ isActive }) =>
                      cn(
                        isActive &&
                          "bg-sidebar-accent text-sidebar-accent-foreground",
                      )
                    }
                  >
                    <ClipboardCheck className="size-4" />
                    <span>Reviewing</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/jobs"
                    className={({ isActive }) =>
                      cn(
                        isActive &&
                          "bg-sidebar-accent text-sidebar-accent-foreground",
                      )
                    }
                  >
                    <Briefcase className="size-4" />
                    <span>Jobs</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/scrapes"
                    className={({ isActive }) =>
                      cn(
                        isActive &&
                          "bg-sidebar-accent text-sidebar-accent-foreground",
                      )
                    }
                  >
                    <Globe className="size-4" />
                    <span>Scrapes</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <Collapsible
                defaultOpen={isSourcesActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton>
                      <RadioTower className="size-4" />
                      <span>Sources</span>
                      <ChevronDown className="ml-auto size-4 hidden group-data-[state=open]/collapsible:inline" />
                      <ChevronRight className="ml-auto size-4 group-data-[state=open]/collapsible:hidden" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/sources/config"}
                        >
                          <NavLink
                            to="/sources/config"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Config
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/sources/featured"}
                        >
                          <NavLink
                            to="/sources/featured"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Featured
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/sources/coupons"}
                        >
                          <NavLink
                            to="/sources/coupons"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Coupons
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={
                            location.pathname === "/sources/external-reviews"
                          }
                        >
                          <NavLink
                            to="/sources/external-reviews"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            External Reviews
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              <Collapsible
                defaultOpen={isProductsActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton>
                      <Package className="size-4" />
                      <span>Products</span>
                      <ChevronDown className="ml-auto size-4 hidden group-data-[state=open]/collapsible:inline" />
                      <ChevronRight className="ml-auto size-4 group-data-[state=open]/collapsible:hidden" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/products/viewer"}
                        >
                          <NavLink
                            to="/products/viewer"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Viewer
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/products/featured"}
                        >
                          <NavLink
                            to="/products/featured"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Featured
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              <Collapsible
                defaultOpen={isUsersActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton>
                      <Users className="size-4" />
                      <span>Users</span>
                      <ChevronDown className="ml-auto size-4 hidden group-data-[state=open]/collapsible:inline" />
                      <ChevronRight className="ml-auto size-4 group-data-[state=open]/collapsible:hidden" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={
                            location.pathname === "/users/subscriptions"
                          }
                        >
                          <NavLink
                            to="/users/subscriptions"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Subscriptions
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={
                            location.pathname === "/users/deletion-requests"
                          }
                        >
                          <NavLink
                            to="/users/deletion-requests"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Deletion Requests
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/users/create"}
                        >
                          <NavLink
                            to="/users/create"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Create User
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              <Collapsible
                defaultOpen={isBusinessesActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton>
                      <Building2 className="size-4" />
                      <span>Businesses</span>
                      <ChevronDown className="ml-auto size-4 hidden group-data-[state=open]/collapsible:inline" />
                      <ChevronRight className="ml-auto size-4 group-data-[state=open]/collapsible:hidden" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/businesses"}
                        >
                          <NavLink
                            to="/businesses"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Accounts
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/newsletter"}
                        >
                          <NavLink
                            to="/newsletter"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            <Newspaper className="size-4" />
                            Newsletter
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/overrides"
                    className={({ isActive }) =>
                      cn(
                        isActive &&
                          "bg-sidebar-accent text-sidebar-accent-foreground",
                      )
                    }
                  >
                    <SlidersHorizontal className="size-4" />
                    <span>Overrides</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/capabilities"
                    className={({ isActive }) =>
                      cn(
                        isActive &&
                          "bg-sidebar-accent text-sidebar-accent-foreground",
                      )
                    }
                  >
                    <ShieldCheck className="size-4" />
                    <span>Capabilities</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/site-gate"
                    className={({ isActive }) =>
                      cn(
                        isActive &&
                          "bg-sidebar-accent text-sidebar-accent-foreground",
                      )
                    }
                  >
                    <Construction className="size-4" />
                    <span>Site Gate</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/email-testing"
                    className={({ isActive }) =>
                      cn(
                        isActive &&
                          "bg-sidebar-accent text-sidebar-accent-foreground",
                      )
                    }
                  >
                    <Mail className="size-4" />
                    <span>Email Testing</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <Collapsible
                defaultOpen={isSimulationActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton>
                      <Eye className="size-4" />
                      <span>Simulation</span>
                      <ChevronDown className="ml-auto size-4 hidden group-data-[state=open]/collapsible:inline" />
                      <ChevronRight className="ml-auto size-4 group-data-[state=open]/collapsible:hidden" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={
                            location.pathname === "/simulation" &&
                            !location.search.includes("tab=fidelity")
                          }
                        >
                          <NavLink
                            to="/simulation?tab=preview"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Preview
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.search.includes("tab=fidelity")}
                        >
                          <NavLink
                            to="/simulation?tab=fidelity"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Fidelity Testing
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/contextual-sim"}
                        >
                          <NavLink
                            to="/contextual-sim"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Contextual Sim
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === "/catalog-runs"}
                        >
                          <NavLink
                            to="/catalog-runs"
                            className={({ isActive }) =>
                              cn(
                                isActive &&
                                  "bg-sidebar-accent text-sidebar-accent-foreground",
                              )
                            }
                          >
                            Catalog Runs
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      className={({ isActive }) =>
                        cn(
                          isActive &&
                            "bg-sidebar-accent text-sidebar-accent-foreground",
                        )
                      }
                    >
                      <item.icon className="size-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-muted-foreground hover:text-foreground"
                  onClick={() => logout()}
                >
                  <LogOut className="size-4" />
                  <span>Log out</span>
                </Button>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
